import {
  type Variant,
  type Personalization,
  type PersonalizationType,
  type ABTestPageConfig,
  type UTMKeys,
} from '../../../netlify/edge-functions/lib/types.ts';
import { TOTAL_WEIGHT } from '../../../netlify/edge-functions/lib/constants.ts';

/*
 * Utils shared with Gatsby
 */

export const getRandomVariant = (variants: Variant[]): Variant | null => {
  // Get a random number between 1 and 100
  const userScore = Math.floor(Math.random() * TOTAL_WEIGHT) + 1;

  let maxWeight = 0;
  let minWeight = 0;

  for (const variant of variants) {
    maxWeight += variant.weight;

    // Loop sequence examples with 3 variants having weights 10, 20 and 40, where X is the userScore:
    // 0 < X <= 10, 10 < X <= 30, 30 < X <= 70
    if (userScore > minWeight && userScore <= maxWeight) {
      return variant;
    }

    minWeight = maxWeight;
  }

  console.log(`getRandomVariant: Could not match any variant in the A/B test. This is a bug.`);
  return null;
};

export const shouldExecuteABTest = ({
  personalizations,
  logicalOperator,
  firstPartyData,
  isReturningVisitor,
}: {
  personalizations: Personalization[];
  logicalOperator: ABTestPageConfig['logicalOperator'];
  firstPartyData: Record<PersonalizationType, string>;
  isReturningVisitor: boolean;
}): boolean => {
  // NOTE If no personalizations are set or if the user has already been assigned a variant, do not abort
  if (personalizations.length === 0 || isReturningVisitor) {
    return true;
  }

  if (logicalOperator === 'and') {
    // NOTE Abort if not all criteria are met
    return personalizations.every((personalization) => {
      const { type, value } = personalization;
      return firstPartyData[type] === value;
    });
  }

  // NOTE logicalOperator === 'or'
  return personalizations.some((personalization) => {
    const { type, value } = personalization;
    return firstPartyData[type] === value;
  });
};

/*
 * Shared functions with utils/a-b-test/getFirstPartyData.ts
 */
export const getUtmParams = (url: URL) => {
  const searchParams = url.searchParams;
  const utmParams: Record<UTMKeys, string> = {
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
  };

  searchParams.forEach((value, key) => {
    if (key in utmParams) {
      utmParams[key as UTMKeys] = value;
    }
  });

  return utmParams;
};

export const isUserAgentMobile = (userAgent: string) => {
  // SOURCE: https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_tablet_or_desktop
  return userAgent.includes('Mobi');
};
